export enum APIEndpoints {
    /** Create Zendesk support ticket */
    ZENDESK_TICKET = '/zendesk/ticket',
    /** Log user route visit */
    USER_ROUTE_VISIT = '/users/log-route',
}

export enum PollingStatus {
    IN_PROGRESS = 'in_progress',
    COMPLETED = 'completed',
    FAILED = 'failed',
}

/**
 * API endpoints for Microservice
 */
export enum MicroserviceAPIEndpoints {
    /** Search for Clients endpoint */
    CLIENT_SEARCH = '/salesforce/opportunities/client/search',
    /** Client operations endpoint */
    CLIENT = '/salesforce/opportunities/client',
    /** Search for Opportunities endpoint */
    OPPORTUNITY_SEARCH = '/salesforce/opportunities/search',
    /** Opportunity operations endpoint */
    OPPORTUNITY = '/salesforce/opportunities',
}

export enum LocalAPIEndpoints {
    ENCRYPT_DASHBOARD = '/api/encrypt/dashboard',
    ENCRYPT_BUILDER = '/api/encrypt/builder',
    GRANT_DASHBOARD_ACCESS = '/api/sigma/grant-access',
    CHECK_DASHBOARD_ACCESS = '/api/sigma/check-access',
    GET_SIGMA_TOKEN = '/api/sigma/token',
    TEMPLATE_SCOPE = '/api/dashboards/template-scope',
}

/**
 * Query keys for React Query cache management
 * Using constant arrays ensures consistent cache keys
 */
export const QUERY_KEYS = {
    /** Navigation page data with optional slug parameter */
    NAVIGATION: (slug?: string) => ['studio', 'navigation', slug] as const,
} as const;

/**
 * Configuration for query stale time and garbage collection
 */
export const QUERY_CONFIG = {
    /** Time in milliseconds before data is considered stale (5 minutes) */
    STALE_TIME: 5 * 60 * 1000,
    /** Time in milliseconds before unused data is garbage collected (30 minutes) */
    GC_TIME: 30 * 60 * 1000,
} as const;

/**
 * Types for sidebar navigation items
 */
export enum SidebarItemType {
    /** Standard link item */
    Link = 'link',
    /** Nested dropdown containing sub-items */
    Nest = 'nest',
    /** Section header (non-clickable) */
    Section = 'section',
}

export const TOAST_CONFIG = {
    position: 'top-right' as const,
    pauseOnFocusLoss: false,
    autoClose: 3000,
    hideProgressBar: true,
};

/** Lucide icons used by Studio nav/home (Iconify: `lucide:{name}`). */
export const STUDIO_LUCIDE_ICONS = [
    'home',
    'bot',
    'sparkles',
    'lightbulb',
    'file-text',
    'map-pin-check',
    'activity',
    'clipboard',
    'hammer',
    'table-2',
    'file-text',
    'database-zap',
    'file-chart-column',
    'library',
    'chart-no-axes-column',
    'fingerprint',
    'layout-dashboard',
    'user',
    'user-plus',
    'map-pin',
    'smartphone',
    'laptop',
    'arrow-right-left',
    'trending-up',
    'medal',
    'trophy',
    'crown',
] as const;

export type StudioLucideIcon = (typeof STUDIO_LUCIDE_ICONS)[number];

/** Feature-flag keys that gate formerly CMS Internal / client-only nav items. */
export const STUDIO_NAV_FEATURE_FLAGS = {
    FEASIBILITY: 'studio.nav.feasibility',
    STUDIO_CATALOG: 'studio.nav.studio-catalog',
    // Backend flag record for the legacy sidebar's Slide Creation card. Who may
    // open Slide Creator is `slide-creator-alpha` (src/lib/slide-creation/alpha-access.ts).
    SLIDE_CREATION: 'studio.nav.report-generation',
    ABC_DTS_NORMS: 'studio.nav.abc-dts-norms',
    REPORTS: 'studio.nav.reports',
    /**
     * Discover → Spotlight (cache-mined weekly insights). Gates the nav
     * card, the /discover/insights route, and the Spotlight Insights
     * dropdown on the Ask DANI landing page — mirror of how REPORTS gates
     * its three surfaces.
     */
    SPOTLIGHT: 'studio.nav.spotlight',
    /**
     * Grandfather switch for the Data Products section (Current Market Trends,
     * Brand Image). Granted to every org that existed at rollout; new orgs never
     * receive it, so the section is absent for them entirely. The products are
     * slated for sunset — when that happens, drop the two cards and this key.
     */
    DATA_PRODUCTS: 'studio.nav.data-products',
} as const;

export type StudioNavFeatureFlag =
    (typeof STUDIO_NAV_FEATURE_FLAGS)[keyof typeof STUDIO_NAV_FEATURE_FLAGS];

export const STUDIO_NAV_FEATURE_FLAG_KEYS: readonly StudioNavFeatureFlag[] =
    Object.values(STUDIO_NAV_FEATURE_FLAGS);
